import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Fragment } from 'react';
import {
  Menu as MenuIcon,
  X,
  Home,
  Briefcase,
  Book,
  Mail,
  LogIn,
  User,
  LayoutDashboard,
  FolderOpen,
  Settings,
  LogOut,
  Plus,
  Moon,
  Sun,
  Palette,
  PartyPopper,
  Download,
  Search,
  Gift
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useQuery } from 'react-query';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import PlatformUpdates from '../dashboard/PlatformUpdates';
import { usePWA } from '../../hooks/usePWA';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme, allowThemeSwitch } = useTheme();
  const { isInstallable, isInstalled, installApp } = usePWA();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = React.useRef(null);

  // Close mobile menu on click/touch outside or escape key
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  // Auto-close on page navigation
  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isAdmin = user?.role === 'admin';

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Services', href: '/services', icon: Briefcase },
    { name: 'Portfolio', href: '/portfolio', icon: Palette },
    { name: 'Refer & Earn', href: '/refer', icon: Gift },
    { name: 'About', href: '/about', icon: Book },
    { name: 'Contact', href: '/contact', icon: Mail },
  ];

  const [logoError, setLogoError] = useState(false);

  const { data: settingsData, isLoading: isSettingsLoading } = useQuery(
    'global-settings',
    () => axios.get('/api/settings').then(res => res.data.data),
    { 
      refetchOnWindowFocus: true, 
      staleTime: 1000,
      initialData: () => {
        const cached = localStorage.getItem('epic-edge-settings');
        return cached ? JSON.parse(cached) : undefined;
      }
    }
  );

  // Sync to localStorage
  React.useEffect(() => {
    if (settingsData) {
      localStorage.setItem('epic-edge-settings', JSON.stringify(settingsData));
    }
  }, [settingsData]);

  // Reset logo error when settings change
  React.useEffect(() => {
    setLogoError(false);
  }, [settingsData?.siteLogoUrl]);

  const resolveLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return getApiUrl(url);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 dark:bg-onyx-900/95 backdrop-blur-sm shadow-sm flex flex-col transition-colors duration-300">
      {settingsData?.announcementBanner && (
        <div className="w-full bg-slate-900 border-b border-white/10 text-amber-500 text-center py-2 px-4 shadow-md text-xs sm:text-xs font-black tracking-widest uppercase flex justify-center items-center gap-2 z-50 relative">
          <PartyPopper className="w-3.5 h-3.5" />
          {settingsData.announcementBanner}
        </div>
      )}
      <div className="w-full border-b border-amber-100 dark:border-onyx-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-3 hover:scale-105 transition-transform duration-300">
                <div className={`flex items-center justify-center transition-all duration-300 ${
                  (settingsData?.siteLogoUrl && !logoError)
                    ? 'w-auto h-12' 
                    : 'w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl shadow-lg'
                }`}>
                  {(settingsData?.siteLogoUrl && !logoError) ? (
                    <img
                      src={resolveLogoUrl(settingsData.siteLogoUrl)}
                      alt="EpicEdge Creative"
                      className="h-10 w-auto object-contain"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    /* Only show fallback if definitely not loading AND no logo exists, or if loading failed */
                    (!isSettingsLoading || logoError) && (
                      <span className="text-lg font-bold text-white">EC</span>
                    )
                  )}
                </div>
                <div className="hidden sm:block">
                  <div className="text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">
                    EpicEdge Creative
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 -mt-1">Modern Software Craftsmanship</div>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${isActive(item.href)
                    ? 'text-amber-600 font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-500'
                    } px-3 py-2 text-sm font-medium transition-colors duration-200 relative group`}
                >
                  {item.name}
                  <span className={`${isActive(item.href) ? 'w-full' : 'w-0 group-hover:w-full'
                    } absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-amber-500 to-yellow-600 transition-all duration-300`}></span>
                </Link>
              ))}
            </div>

            {/* Desktop User Menu */}
            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                <>
                  <PlatformUpdates />
                  {allowThemeSwitch && (
                    <button
                      onClick={toggleTheme}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                      title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Onyx Mode'}
                    >
                      {isDarkMode ? <Sun className="w-5 h-5 shadow-sm" /> : <Moon className="w-5 h-5 shadow-sm" />}
                    </button>
                  )}
                  
                  <button
                    onClick={() => window.dispatchEvent(new Event('open_command_center'))}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                    title="Command Center (Ctrl+K)"
                  >
                    <Search className="w-5 h-5 shadow-sm" />
                  </button>

                  <Link
                    to="/dashboard"
                    className="bg-amber-50 dark:bg-onyx-800 text-amber-700 dark:text-amber-500 hover:bg-amber-100 dark:hover:bg-onyx-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Dashboard
                  </Link>
                  
                  {isInstallable && !isInstalled && (
                    <button
                      onClick={installApp}
                      className="flex items-center gap-2 bg-slate-800 dark:bg-onyx-800 text-amber-500 hover:bg-slate-700 dark:hover:bg-onyx-700 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 hover:scale-105 shadow-md border border-amber-500/20"
                    >
                      <Download className="w-4 h-4" />
                      Install App
                    </button>
                  )}

                  {/* User Profile Dropdown / Link */}
                  <div className="flex items-center space-x-3 border-l border-gray-200 pl-4 ml-2">
                    <Link to="/profile" className="flex items-center gap-2 group">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center group-hover:shadow-md transition-all">
                        <span className="text-sm font-bold text-white">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </Link>
                    <button
                      onClick={logout}
                      className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 px-3 py-2 text-sm font-medium transition-colors duration-200"
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <PlatformUpdates />
                  <Link
                    to="/login"
                    className="text-gray-700 dark:text-gray-300 hover:text-amber-700 dark:hover:text-amber-500 px-4 py-2 text-sm font-medium transition-all duration-300 border border-gray-300 dark:border-onyx-700 hover:border-amber-400 dark:hover:border-amber-500 rounded-lg hover:bg-amber-50 dark:hover:bg-onyx-800"
                  >
                    Client Portal
                  </Link>
                  {allowThemeSwitch && (
                    <button
                      onClick={toggleTheme}
                      className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all duration-200"
                      title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Onyx Mode'}
                    >
                      {isDarkMode ? <Sun className="w-5 h-5 shadow-sm" /> : <Moon className="w-5 h-5 shadow-sm" />}
                    </button>
                  )}
                  {isInstallable && !isInstalled && (
                    <button
                      onClick={installApp}
                      className="flex items-center gap-2 border border-slate-300 dark:border-onyx-700 text-slate-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-500 hover:border-amber-400 dark:hover:border-amber-500 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 hover:bg-amber-50 dark:hover:bg-onyx-800"
                    >
                      <Download className="w-4 h-4" />
                      Install App
                    </button>
                  )}

                  <Link
                    to="/contact"
                    className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Get Quote
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center gap-1.5">
              <PlatformUpdates />
              {isInstallable && !isInstalled && (
                <button
                  onClick={installApp}
                  className="p-2 text-slate-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                  title="Install App"
                >
                  <Download className="w-5 h-5 shadow-sm" />
                </button>
              )}
              {allowThemeSwitch && (
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Onyx Mode'}
                >
                  {isDarkMode ? <Sun className="w-5 h-5 shadow-sm" /> : <Moon className="w-5 h-5 shadow-sm" />}
                </button>
              )}
              
              <button
                onClick={() => window.dispatchEvent(new Event('open_command_center'))}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-lg transition-all duration-200"
                title="Search"
              >
                <Search className="w-5 h-5 shadow-sm" />
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-onyx-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 transition-colors duration-200"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <MenuIcon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {/* Menu Panel */}
          <div
            ref={mobileMenuRef}
            className="fixed top-0 right-0 h-auto max-h-[90vh] w-64 bg-white dark:bg-onyx-900 shadow-2xl rounded-bl-3xl overflow-hidden border-l border-b border-amber-100 dark:border-onyx-800 transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-500 to-yellow-600">
              <div>
                <h3 className="text-lg font-bold text-white">Menu</h3>
                <p className="text-xs text-amber-100">Navigate EpicEdge</p>
              </div>
              <div className="flex items-center gap-2">
                {allowThemeSwitch && (
                  <button
                    onClick={toggleTheme}
                    className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"
                  >
                    {isDarkMode ? <Sun className="w-4 h-4 text-white" /> : <Moon className="w-4 h-4 text-white" />}
                  </button>
                )}
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Menu Content */}
            <div className="p-4 bg-white dark:bg-onyx-900">
              {/* Navigation Links */}
              <div className="space-y-2">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-3 text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-xl transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Home className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="font-semibold">Home</span>
                </Link>
                <Link
                  to="/services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-3 text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-xl transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-4.5 h-4.5 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="font-semibold">Services</span>
                </Link>
                <Link
                  to="/portfolio"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-3 text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-xl transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Palette className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="font-semibold">Portfolio</span>
                </Link>
                <Link
                  to="/refer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-3 text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-xl transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                    <Gift className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="font-semibold">Refer & Earn</span>
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-3 text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-xl transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                    <Book className="w-4.5 h-4.5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span className="font-semibold">About</span>
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-3 text-gray-800 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-onyx-800 rounded-xl transition-colors duration-200"
                >
                  <div className="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <Mail className="w-4.5 h-4.5 text-red-600 dark:text-red-400" />
                  </div>
                  <span className="font-semibold">Contact</span>
                </Link>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-onyx-800 space-y-2">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full p-4 text-center text-amber-700 dark:text-amber-500 border border-amber-300 dark:border-onyx-700 rounded-xl font-bold hover:bg-amber-50 dark:hover:bg-onyx-800 transition-colors duration-200"
                >
                  Client Portal
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full p-4 text-center bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-xl font-bold hover:from-amber-600 hover:to-yellow-700 shadow-lg transition-all duration-200"
                >
                  Get Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
